<template>
    <AppModal :title="$t('labels.update_group_product_data')" ref="modal" size="full" @onHide="hide">
        <div class="modal-body table-responsive">
            <Alert type="warning" v-if="Object.keys(selectedProducts).length === 0">
                {{ $t('alerts.no_products_selected') }}
            </Alert>
            <TaskProgressBar v-else-if="task.state.isRunning" :done="task.state.done" :total="task.state.total" :expected-steps="20"/>
            <template v-else-if="!task.state.isRunning && !Object.keys(updateRequests).length">
                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th>{{ $t('labels.field') }}</th>
                            <th width="130px">
                                <div class="d-flex align-items-center">
                                    <AppCheckbox :model-value="allValueIsChecked" @update:model-value="toggleSelectAll" class="mr-2" /> {{ $t('labels.update') }}
                                </div>
                            </th>
                            <th width="270px">
                                <div class="d-flex align-items-center">
                                    <AppCheckbox :model-value="allAiAdaptationIsChecked" @update:model-value="toggleSelectAllAiAdaptation" class="mr-2" /> {{ $t('labels.ai_adaptation') }}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="field in fieldsForUpdate" :key="field.key">
                            <td>{{ field.title }}</td>
                            <td>
                                <AppCheckbox v-model="field.update" @update:model-value="handleUpdateChange(field)" />
                            </td>
                            <td>
                                <AppCheckbox v-model="field.aiAdaptation" @update:model-value="handleAiAdaptationChange(field)" :disabled="!adaptableFields.includes(field.key)" />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Alert type="warning" v-if="hasAiAdaptationSelected && tokenBalance !== null && tokenBalance <= 0" class="mt-3">
                    {{ $t('alerts.insufficient_ai_balance') }}
                </Alert>
            </template>
            <template v-else v-for="(request, productUuid, index) in updateRequests">
                <GeneratedContentFields
                    v-if="step === index"
                    :languages="integration.language_aliases"
                    :product-body="request.body"
                    :product="selectedProducts[productUuid]"
                    :step="step + 1"
                    :total-steps="Object.keys(updateRequests).length"
                    :fields-for-update="fieldsForUpdate"
                />
            </template>
            <Alert v-if="balanceExhaustedDuringProcessing && !task.state.isRunning && Object.keys(updateRequests).length" type="warning" class="mt-3 mb-0">
                {{ $t('alerts.ai_balance_exhausted_during_processing') }}
            </Alert>
        </div>
        <div class="modal-footer pt-3" v-if="!task.state.isRunning">
            <AppButton color="indigo" @click="updateProductData" :disabled="!someOneFieldNeedUpdate || (hasAiAdaptationSelected && tokenBalance !== null && tokenBalance <= 0)" v-if="!Object.keys(updateRequests).length">
                {{ $t('buttons.update') }}
            </AppButton>
            <AppButton color="indigo" @click="nextStep" v-else>
                {{ step === Object.keys(updateRequests).length - 1 ? $t('buttons.finish') : $t('buttons.next') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';
import GeneratedContentFields from './generatedContentFields/GeneratedContentFields.vue';
import Alert from '../../../../components/AlertComponent.vue';

import { httpClient } from '../../../../services/HttpClient';
import HttpRequest from '../../../../services/Http/HttpRequest';
import HttpSender from '../../../../services/Http/HttpSender';
import GeminiService from '../../../../services/GeminiService/GeminiService';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';
import { TooManyRequestsError } from '../../../../services/Errors/TooManyRequestsError';
import { ServiceUnavailableError } from '../../../../services/Errors/ServiceUnavailableError';
import MessageNotificator from '../../../../services/MessageNotificator';

export default {
    name: 'UpdateGroupProductDataModal',
    components: {
        AppModal, AppCheckbox, AppButton, FormGroup, TaskProgressBar, GeneratedContentFields, Alert
    },
    emits: ['products-updated'],
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            adaptableFields: ['names', 'descriptions', 'seos'],
            selectedProducts: {},
            fieldsForUpdate: [
                {title: this.$t('labels.name'),         key: 'names',        update: false, aiAdaptation: false },
                {title: this.$t('labels.description'),  key: 'descriptions', update: false, aiAdaptation: false },
                {title: this.$t('labels.seo'),          key: 'seos',         update: false, aiAdaptation: false },
                {title: this.$t('labels.price'),        key: 'price',        update: false, aiAdaptation: false },
                {title: this.$t('labels.sale_price'),   key: 'sale_price',   update: false, aiAdaptation: false },
                {title: this.$t('labels.balance'),      key: 'balance',      update: false, aiAdaptation: false },
                {title: this.$t('labels.manufacturer'), key: 'manufacturer', update: false, aiAdaptation: false },
                {title: this.$t('labels.measurement'),  key: 'measurement',  update: false, aiAdaptation: false },
                {title: this.$t('labels.images'),       key: 'images',       update: false, aiAdaptation: false },
            ],
            updateRequests: {},
            task: createProgressTask(),
            step: 0,
            prompt: '',
            tokenBalance: null,
            balanceExhaustedDuringProcessing: false,
        };
    },
    methods: {
        handleUpdateChange(field) {
            if (!field.update && field.aiAdaptation) {
                field.aiAdaptation = false;
            }
        },
        handleAiAdaptationChange(field) {
            if (field.aiAdaptation && !field.update) {
                field.update = true;
            }
        },
        async show(selectedProducts) {
            let products = {...selectedProducts};
            if (products.hasOwnProperty(this.$route.params.integrationProductUuid)) {
                delete products[this.$route.params.integrationProductUuid];
            }

            this.selectedProducts = products;
            this.$refs.modal.show();

            if (this.$route.params.projectUuid) {
                const balanceResponse = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
                this.tokenBalance = balanceResponse.data.balance;
            }
        },
        hide() {
            this.$refs.modal.hide();
            this.selectedProducts = {};
            this.fieldsForUpdate.forEach(field => {
                field.update = false;
                field.aiAdaptation = false;
            });
            this.updateRequests = {};
            this.task.cancel();
            this.step = 0;
            this.tokenBalance = null;
            this.balanceExhaustedDuringProcessing = false;
        },
        async nextStep() {
            if (this.step < Object.keys(this.updateRequests).length - 1) {
                this.step++;
            } else {
                await this.sendUpdateRequests(Object.values(this.updateRequests));
                this.hide();
            }
        },
        async sendUpdateRequests(updateRequests) {
            await new HttpSender().sendRequestsInBatches(updateRequests, () => {});
            this.$emit('products-updated');
        },
        async updateProductData() {
            let shouldHide = false;

            const completed = await this.task.run(async (progress) => {
                const currentProductData = await this.fetchCurrentProductData();

                const hasAiAdaptation = this.fieldsForUpdate.some(field => field.aiAdaptation);

                if (!hasAiAdaptation) {
                    const updateRequests = await this.collectUpdateRequests(currentProductData, progress);

                    await this.sendUpdateRequests(updateRequests);

                    shouldHide = true;

                    return;
                }

                await this.fetchPrompt();

                const updateRequests = await this.collectUpdateRequestsWithAdaptationContent(currentProductData, progress);

                if (updateRequests) {
                    this.updateRequests = updateRequests
                }
            });

            if (completed && shouldHide) {
                this.hide();
            }
        },
        async fetchCurrentProductData() {
            return await httpClient.get(`/integrations/products/${this.$route.params.integrationProductUuid}`).then(response => {
                return response.data.data;
            })
        },
        toggleSelectAll(checked) {
            if (checked) {
                this.fieldsForUpdate.forEach(field => {
                    field.update = true;
                });
            } else {
                this.fieldsForUpdate.forEach(field => {
                    field.update = false;
                    field.aiAdaptation = false;
                });
            }
        },
        toggleSelectAllAiAdaptation(checked) {
            if (checked) {
                this.fieldsForUpdate.forEach(field => {
                    if (this.adaptableFields.includes(field.key)) {
                        field.aiAdaptation = true;
                        field.update = true;
                    }
                });
            } else {
                this.fieldsForUpdate.forEach(field => {
                    if (this.adaptableFields.includes(field.key)) {
                        field.aiAdaptation = false;
                    }
                });
            }
        },
        async collectUpdateRequestsWithAdaptationContent(currentProductData, {tick}) {
            const updateRequests = {};
            let requestBody = {};
            let fieldsForAdaptation = [];

            for (const field of this.fieldsForUpdate) {
                if (!field.update) continue;

                if (field.key === 'manufacturer') {
                    requestBody['manufacturer_name'] = currentProductData[field.key];
                    continue;
                }

                requestBody[field.key] = currentProductData[field.key];

                if (field.aiAdaptation && this.adaptableFields.includes(field.key)) {
                    fieldsForAdaptation.push(field.key);
                }
            }

            let geminiService = null;

            geminiService = new GeminiService();

            let productFieldsForAiAdaptation = {};

            productFieldsForAiAdaptation = await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                uuids: Object.keys(this.selectedProducts),
                extra_fields: ['integrationFilterItems']
            }).then(response => {
                return response.data.data.reduce((acc, product) => {
                    acc[product.uuid] = {
                        images: product.images,
                        filter_items: product.integration_filter_items.map(item => (`${item.integration_filter.name}: ${item.name}`))
                    };
                    return acc;
                }, {});
            });

            try {
                for (const [productUuid, fields] of Object.entries(productFieldsForAiAdaptation)) {
                    let aiResponse = {};

                    if (geminiService && fieldsForAdaptation.length > 0) {
                        let prompt = this.createPrompt(fields, requestBody, fieldsForAdaptation);
                        try {
                            aiResponse = await geminiService.generate(prompt);
                            if (!aiResponse || typeof aiResponse !== 'object') {
                                aiResponse = {};
                            }
                        } catch (error) {
                            if (error instanceof TooManyRequestsError) {
                                new MessageNotificator().warning(this.translate('alerts.too_many_requests'));
                            } else if (error instanceof ServiceUnavailableError) {
                                new MessageNotificator().error(this.translate('alerts.service_unavailable'));
                            } else {
                                new MessageNotificator().error(this.translate('alerts.contact_support'));
                            }
                            return;
                        }
                    }
                    const clonedRequestBody = JSON.parse(JSON.stringify(requestBody || {}));

                    for (let [key, value] of Object.entries(aiResponse)) {
                        clonedRequestBody[key] = value;
                    }

                    updateRequests[productUuid] = new HttpRequest('PATCH', `/integrations/products/${productUuid}`, clonedRequestBody);

                    tick();

                    if (geminiService) {
                        await geminiService.flushTokens(this.$route.params.projectUuid, 'Адаптація даних товарів (масова дія)');
                        await this.refreshBalance();
                        if (this.tokenBalance !== null && this.tokenBalance <= 0) {
                            this.balanceExhaustedDuringProcessing = true;
                            break;
                        }
                    }
                }
            } finally {}

            return updateRequests;
        },
        async collectUpdateRequests(currentProductData, {tick}) {
            const updateRequests = [];
            let requestBody = {};

            for (const field of this.fieldsForUpdate) {
                if (!field.update) continue;

                if (field.key === 'manufacturer') {
                    requestBody['manufacturer_name'] = currentProductData[field.key];
                    continue;
                }

                requestBody[field.key] = currentProductData[field.key];
            }

            for (const productUuid of Object.keys(this.selectedProducts)) {
                updateRequests.push(
                    new HttpRequest('PATCH', `/integrations/products/${productUuid}`, requestBody)
                );
                tick();
            }

            return updateRequests;
        },
        createPrompt(fields, requestBody, fieldsForAdaptation) {
            let prompt = this.prompt;

            prompt += `As a result, you need to return an object in json format, nothing superfluous, no changes, just json: {`

            for (const field of fieldsForAdaptation) {
                if (requestBody[field]) {
                    prompt += `\n${field}: ${field} after adaptation (only if ${field} are selected for update, always an object)`;
                }
            }

            prompt += `\n} Current Product Data:`;

            for (const field of fieldsForAdaptation) {
                if (requestBody[field]) {
                    prompt += `\n${field}: ${JSON.stringify(requestBody[field])}`;
                }
            }

            prompt += `\nProduct Attributes: ${JSON.stringify(fields.filter_items)}`;
            prompt += `\nProduct Images: ${JSON.stringify(fields.images)}`;

            return prompt;
        },
        async fetchPrompt() {
            const response = await httpClient.post(`/proxy/http-request`, {
                method: "GET",
                url: "https://fasunok.s3.eu-west-2.amazonaws.com/system/gemini-prompts/adaptation.json"
            });

            if (response.status === 200) {
                this.prompt = response.data.text;
            }
        },
        async refreshBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        }
    },
    computed: {
        someOneFieldNeedUpdate() {
            return this.fieldsForUpdate.some(field => field.update);
        },
        hasAiAdaptationSelected() {
            return this.fieldsForUpdate.some(field => field.aiAdaptation);
        },
        allValueIsChecked() {
            return this.fieldsForUpdate.every(field => field.update);
        },
        allAiAdaptationIsChecked() {
            const adaptableFields = this.fieldsForUpdate.filter(field =>
                this.adaptableFields.includes(field.key)
            );
            return adaptableFields.length > 0 && adaptableFields.every(field => field.aiAdaptation);
        }
    },
    beforeUnmount() {
        this.task.cancel();
    }
}
</script>
